import { FouSelectObject } from 'src/utils/objects/fou-select'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import { errors as submissionErrors } from 'src/pages/submissions/script/common.data'
import { ceil } from 'lodash'

export default {
  isSet() {
    return (
      this.states.conversions === 'set' &&
      this.states.families === 'set' &&
      this.states.custom === 'set' &&
      this.hasBinaries()
    )
  },
  hasBinaries() {
    return this.submissionBinaries.length > 0
  },
  getStoreBinary(b) {
    const binary = this.$store.getters['binaries/list'].filter(
      item => item.id === b.binaryId && typeof item.id === 'string' && item.fontInfoComplete === true
    )[0]
    if (binary) {
      binary.styleName = b.styleName || binary.styleName
    }
    return binary
  },
  getBinaryProofApiPackage(pack) {
    /**
     * Does not work for fou-selects, but for api styles.
     */
    const styles = []
    pack.styles.map(style => {
      if (typeof this.getStoreBinary(style) === 'object') {
        style.binaryName = this.getSubmissionBinary(style.binaryId).styleName
        styles.push(style)
      }
    })
    pack.styles = styles
    return pack
  },
  getCurrencyConversions() {
    return this.$store.dispatch('packages/getCurrencyConversions')
  },
  createPackage(options) {
    options = {
      name: '',
      type: 'familyPackage',
      prices: [],
      styles: [],
      editing: false,
      externalSku: '',
      skuId: '0',
      oldSku: '0',
      ...options
    }
    /**
     * Return {Object}
     */
    return {
      name: options.name,
      packageType: options.type,
      prices: options.prices,
      styles: options.styles,
      externalSku: options.externalSku,
      skuId: options.skuId,
      oldSku: options.oldSku
    }
  },
  setPackages() {
    return Promise.all([
      new Promise(resolve => {
        this.setFamilyPackage()
          .then(() => {
            this.states.families = 'set'
          })
          .then(resolve)
      }),
      new Promise(resolve => {
        this.setSinglePackages()
          .then(() => {
            this.states.singles = 'set'
          })
          .then(resolve)
      }),
      new Promise(resolve => {
        this.setCustomPackages()
          .then(() => {
            this.states.custom = 'set'
          })
          .then(resolve)
      })
    ])
  },
  setFamilyPackage() {
    return new Promise(resolve => {
      const family = this.submissionPackages.filter(p => p.packageType === 'familyPackage')[0] || {}
      // Check if it is single style exist in package
      const isSingleStylePackageExist =
        this.submissionPackages.findIndex(data => data.packageType === 'singleStyle') > -1
      /**
       * If unset add family package to component data.
       */
      if (this.states.families === 'unset') {
        this.packages.familyPackage = this.createPackage({
          prices: this.getViewPrices(family.prices || [])
        })
      }
      /**
       * Check if the family package is single style family package
       * If true, then store the details in component internal state
       */

      if ((!isSingleStylePackageExist && isNull(this.singleStyleFamilyPack))) {
        this.singleStyleFamilyPack = family
      }
      /**
       * Set family pack name, externalSku, skuId & oldSku.
       */
      this.packages.familyPackage.name = this.getFamilyPackName()
      this.packages.familyPackage.externalSku = family.externalSku === undefined ? '' : family.externalSku
      this.packages.familyPackage.skuId = family.skuId === undefined ? 0 : family.skuId
      this.packages.familyPackage.oldSku = family.oldSku === undefined ? 0 : family.oldSku
      /**
       * Set prices.
       * If editing is true, user is focusing prices. Don’t overwrite/correct at this point.
       */
      if (!this.packages.familyPackage.editing) {
        this.packages.familyPackage.prices = this.getViewPrices(this.packages.familyPackage.prices)
      }
      /**
       * Set styles.
       */
      this.packages.familyPackage.styles = this.getFamilyPackageStyles()
      /**
       * Set image src for font render.
       */
      this.packages.familyPackage.renderSrc = this.getBinaryFontRenderSrc(
        this.getDefaultLayoutFontId(),
        this.getFamilyPackName(),
        40,
        undefined,
        true)
      /**
       * Done.
       */
      resolve()
    })
  },
  getFamilyPackageStyles() {
    /**
     * Set the styles for family packages.
     */
    let styles = []
    /**
     * Find defaultLayoutFont in $store.
     */
    const binaries = this.$store.getters['binaries/list']
    const binary = binaries.filter(b => b.id === this.getDefaultLayoutFontId())[0]
    /**
     * Check if defaultLayoutFont binary is ok.
     */
    if (typeof binary === 'object' && binary.fontInfoComplete === true) {
      styles = binaries.map(style => {
        return {
          binaryId: style.id,
          binaryName: style.styleName
        }
      })
    }
    /**
     * Return {Array}
     */
    return styles
  },
  getApiFamilyPackage() {
    if (this.hasBinaries()) {
      /**
       * Return {Object}
       */
      return this.createPackage({
        name: this.getFamilyPackName(),
        prices: this.getApiPrices(this.packages.familyPackage.prices || []),
        styles: this.getFamilyPackageStyles(),
        externalSku: this.packages.familyPackage.externalSku,
        skuId: this.packages.familyPackage.skuId,
        oldSku: this.packages.familyPackage.oldSku
      })
    }
    /**
     * Return {null}
     */
    return null
  },
  setSinglePackages() {
    return new Promise(resolve => {
      const styles = []
      /**
       * Loop through submission binaries and create a package for each one.
       * Only if submission has more than one binary.
       */
      if (this.submissionBinaries.length === 0) {
        this.lastSingleStyle = null
      }
      if (this.submissionBinaries.length > 1) {
        for (const b of this.submissionBinaries) {
          /**
           * Find completed binary in $store.
           */
          const binary = this.getStoreBinary(b)
          /**
           * Verify that binary is ok.
           */
          if (typeof binary === 'object') {
            /**
             * Check for prices from submission.
             */
            const storeStyle = this.submissionPackages.filter(p => p.packageType === 'singleStyle' && p.styles[0].binaryId === b.binaryId)[0] || {}
            /**
             * Check for prices from this.packages.
             */
            const localStyle = this.packages.singleStyles.filter(s => s.styles[0].binaryId === b.binaryId)[0] || {}
            /**
             * Check if it was lastSingleStyle or not before
             * and if yes then set skuId as lastSingleStyle skuId and set oldSku as 0 if it is the same style
             * but if same style is updated then set skuId as 0 and set oldSku as lastSingleStyle skuId
             */
            if (!isNull(this.lastSingleStyle) && !isEmpty(storeStyle)) {
              this.submissionPackages.forEach(data => {
                if (data.packageType === 'familyPackage') {
                  data.oldSku = this.lastSingleStyle.oldFamilySku
                }
              })
              storeStyle.oldSku = isEqual(this.lastSingleStyle.binaryId, storeStyle.styles[0].binaryId)
                ? 0
                : this.lastSingleStyle.skuId
              storeStyle.skuId = isEqual(this.lastSingleStyle.binaryId, storeStyle.styles[0].binaryId)
                ? this.lastSingleStyle.skuId
                : 0
              this.lastSingleStyle = null // set lastSingleStyle state back to null
            }
            /**
             * Create style.
             */
            const pack =
              localStyle.editing === true
                ? localStyle
                : this.createPackage({
                  name: this.getStyleName(b.styleName, binary),
                  type: 'singleStyle',
                  prices: this.getViewPrices(localStyle.prices || storeStyle.prices || []),
                  styles: [
                    {
                      binaryId: binary.id,
                      binaryName: b.styleName
                    }
                  ],
                  externalSku: storeStyle.externalSku === undefined ? '' : storeStyle.externalSku,
                  skuId: storeStyle.skuId === undefined ? 0 : storeStyle.skuId,
                  oldSku: storeStyle.oldSku === undefined ? 0 : storeStyle.oldSku
                })
            /**
             * Add Render src for name.
             */
            pack.renderSrc = this.getBinaryFontRenderSrc(
              pack.styles[0].binaryId,
              b.styleName,
              40,
              undefined,
              true)
            /**
             * Push to list.
             */
            styles.push(pack)
          }
        }
      }
      /**
       * If only one single style left in submission Binaries then
       * Add last single style skuId, binaryId & binaryName in state
       */
      if (isEqual(this.submissionBinaries.length, 1)) {
        this.submissionPackages.forEach(data => {
          if (isEqual(data.packageType, 'familyPackage') && this.lastSingleStyle !== null) {
            this.lastSingleStyle.oldFamilySku = data.oldSku
            data.skuId = '0'
            data.oldSku = this.lastSingleStyle.skuId
          }
          if (isEqual(data.packageType, 'singleStyle')) {
            const lastSingleStyle = data.styles.find(item => item.binaryId === this.submissionBinaries[0].binaryId)
            if (!isUndefined(lastSingleStyle)) {
              this.lastSingleStyle = {
                skuId: data.skuId,
                ...lastSingleStyle
              }
            }
          }
        })
      }
      this.packages.singleStyles = styles
      /**
       * Check for deleted packages.
       */
      this.packages.singleStyles.forEach((style, idx) => {
        const binary = this.submissionBinaries.filter(b => b.binaryId === style.styles[0].binaryId)[0]
        if (typeof binary !== 'object') {
          this.packages.singleStyles.splice(idx, 1)
        }
      })
      /**
       * If less or only one binary is in $store, remove style packages.
       */
      if (this.$store.getters['binaries/list'].length <= 1) {
        this.packages.singleStyles = []
      }
      /**
       * Done.
       */
      resolve()
    })
  },
  getApiSinglePackages() {
    const singles = []
    for (const single of this.packages.singleStyles) {
      singles.push(
        this.createPackage({
          name: `${this.getFamilyName()} ${this.getSubmissionBinary(single.styles[0].binaryId).styleName}`,
          type: 'singleStyle',
          prices: this.getApiPrices(single.prices),
          styles: single.styles,
          externalSku: single.externalSku,
          skuId: single.skuId,
          oldSku: single.oldSku
        })
      )
    }
    return singles
  },
  setCustomPackages() {
    return new Promise(resolve => {
      /**
       * If only one binary, remove all custom packs.
       */
      if (this.$store.getters['binaries/list'].length <= 2) {
        this.packages.customPackages = []
        resolve()
      }
      /**
       * Get submission data.
       */
      this.getCustomPackages()
        .then(() => {
          return this.setCustomPackagesValues()
        })
        .then(() => {
          return this.setCustomPackagesOptions()
        })
        .then(() => {
          /**
           * Set prices.
           */
          this.packages.customPackages.forEach((pack, idx) => {
            if (!pack.editing && !Object.hasOwn(this.errors, `currency custom${idx}`)) {
              pack.prices = this.getViewPrices(pack.prices)
            }
          })
          this.$forceUpdate()
        })
      /**
       * Done.
       */
      resolve()
    })
  },
  setCustomPackagesValues() {
    return new Promise(resolve => {
      const styleObjects = this.packages.singleStyles.map(item => ({
        value: item.styles[0].binaryId,
        label: item.styles[0].binaryName
      }));

      const customPackageOptions = this.packages.customPackages[0]?.select?.options;
      const currentCustomPackageCount = this.packages.customPackages.length;

      let areStylesEqual = false;

      if (customPackageOptions) {
        areStylesEqual = customPackageOptions.every((item, index) => {
          const correspondingItem = styleObjects[index];
          return correspondingItem && item.label === correspondingItem.label;
        });
      }

      // Check if the count of customPackages has increased
      if (currentCustomPackageCount > this.previousCustomPackageCount) {
        areStylesEqual = false;
      }

      // Set the previousCustomPackagesCount for the next check
      this.previousCustomPackageCount = currentCustomPackageCount;

      if (!areStylesEqual) {
        this.packages.customPackages.forEach((pack, key) => {
          /**
           * Set fou-select.
           */
          if (!pack.select) {
            pack.select = FouSelectObject()
            /**x
             * Set fou-select update callback.
             */
            pack.select.updateCustom = val => {
              pack.select.update(val, this.commitChange)
            }
          }
          /**
           * Only take existing binaries.
           */
          const values = []
          const styles =
            pack.select.values === null
              ? pack.styles
              : pack.select.getApiValues({
                value: 'binaryId',
                name: 'binaryName',
                multiple: true
              })
          styles.forEach(style => {
            const binary = this.getStoreBinary(style)
            if (typeof binary === 'object') {
              values.push(binary)
            }
          })
          /**
           * Set fou-select values.
           */
          pack.select.setValues({
            obj: values,
            value: 'id',
            name: 'styleName',
            multiple: true
          })
          /**
           * Setting 'pack.styles' as blank object so that the original value of the style is not inserted
           * in 'styles' when all the styles are removed from UI.
           */
          pack.styles = []
          /**
           * Validate custom pack
           * when related fou-select changed (pristine===false)
           */
        })
        this.checkDoubles()
        /**
         * Done.
         */
        resolve()
      }
    })
  },
  setCustomPackagesOptions() {
    return new Promise(resolve => {
      const options = []
      /**
       * Get binaries from store in order to submission binary values.
       */
      for (const b of this.submissionBinaries) {
        const binary = this.getStoreBinary(b)
        if (typeof binary === 'object') {
          options.push(binary)
        }
      }

      /**
       * Set options.
       */
      this.packages.customPackages.forEach(pack => {
        if (pack.select) {
          pack.select.setOptions({
            obj: options,
            value: 'id',
            name: 'styleName',
            multiple: true
          })
        }
      })
      /**
       * Done.
       */
      resolve()
    })
  },
  getCustomPackages() {
    return new Promise(resolve => {
      /**
       * If unset create packages from submission.
       */
      if (this.states.custom === 'unset') {
        /**
         * Find packages.
         */
        const customPackages = this.submissionPackages.filter(p => p.packageType === 'customPackage')
        /**
         * Loop through submission customPackages and create a package for each if not set.
         */
        customPackages.forEach((c, index) => {
          /**
           * Set package.
           */
          this.packages.customPackages[index] = this.createPackage({
            name: c.name,
            type: 'customPackage',
            prices: this.getViewPrices(c.prices),
            styles: c.styles,
            externalSku: c.externalSku,
            skuId: c.skuId,
            oldSku: c.oldSku
          })
        })
      }
      /**
       * Done.
       */
      resolve()
    })
  },
  validationMessage() {
    let intialPackageLength = this.submission.userMetaData.packages.filter(item => item.packageType === 'customPackage').length
    let binariesLength = this.list.length
    let submissionStatus = this.submission.status
    if (submissionStatus === 'in_work' || intialPackageLength <= ceil(binariesLength / 4)) {
      return this.$t('pages.submissions.components.prices.custom.description', { package_length: ceil(binariesLength / 4) })
    }
    return ''
  },
  validatePackage(customPackageLength) {
    let intialPackageLength = this.submission.userMetaData.packages.filter(item => item.packageType === 'customPackage').length
    let binariesLength = this.list.length
    let submissionStatus = this.submission.status
    if (intialPackageLength >= ceil(binariesLength / 4) && submissionStatus !== 'in_work') {
      return false
    } else if (customPackageLength < ceil(binariesLength / 4)) {
      return true
    } else {
      return false
    }
  },
  getApiCustomPackages() {
    let customs = []
    for (let custom of this.packages.customPackages) {
      let styles = custom.select.getApiValues({
        value: 'binaryId',
        name: 'binaryName',
        multiple: true
      })
      customs.push(
        this.createPackage({
          name: custom.name,
          type: 'customPackage',
          prices: this.getApiPrices(custom.prices),
          styles: Array.isArray(styles) ? styles : [],
          externalSku: custom.externalSku,
          skuId: custom.skuId !== undefined || custom.skuId !== '' ? custom.skuId : 0,
          oldSku: custom.oldSku !== undefined || custom.oldSku !== '' ? custom.oldSku : 0
        })
      )
    }
    return customs
  },
  addCustomPackage() {
    return new Promise(resolve => {
      this.packages.customPackages.push(
        this.createPackage({
          name: '',
          type: 'customPackage'
        })
      )
      submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
        JSON.parse(i)
      )
      this.setCustomPackages().then(() => {
        this.commitChange()
        submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
          JSON.parse(i)
        )
        resolve()
      })
      submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
        JSON.parse(i)
      )
    })
  },
  removeCustomPackage(index) {
    this.packages.customPackages.forEach((item, key) => {
      this.removeFromSubmissionsFontSubFamilyAndCurrentyErrors(key)
    })
    return new Promise(resolve => {
      this.packages.customPackages.splice(index, 1)
      this.bannedWordForSubpackage.splice(index, 1)
      this.commitChange()
      this.handleCustomPackageValidation()
      resolve()
    })
  },
  /**
   * Remove specific error from global errors.
   */
  removeFromSubmissionsFontSubFamilyAndCurrentyErrors(key) {
    const keysToRemoveFromSubmissionErrors = [
      `font sub family${key} prices`,
      `currency custom${key}`,
      `subpackage ${key} prices`,
      `external sku ${key} prices`
    ]
    keysToRemoveFromSubmissionErrors.forEach(item => {
      this.removeErrorFromSubmisisonErrors(item)
    })
  },
  removeErrorFromSubmisisonErrors(customKey) {
    /**
     * To manually remove the related errors associated with custom subpackage. when delete icon is clicked
     * {error} object is set automatically from useForm() but the global errors in common.data.js is not reset
     * So, deleting each associated keys from the errors array
     */
    const keysExistingInErrors = Object.keys(this.errors).filter(error => error.includes(customKey))
    keysExistingInErrors.forEach(key => {
      submissionErrors.value = submissionErrors.value.filter(error => !Object.hasOwn(error, key))
    })
    submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
      JSON.parse(i)
    )
  },
  getDefaultLayoutFontId() {
    /**
     * Find defaultLayoutFont style or first binary from submission.binaries.
     */
    const requireId =
      typeof this.submissionDefaultLayoutFont.id === 'string' && this.submissionDefaultLayoutFont.id.length > 0
        ? this.submissionDefaultLayoutFont.id
        : this.submissionBinaries?.[0]?.binaryId || ''
    /**
     * Return {String}
     */
    return requireId
  },
  getFamilyName() {
    return this.submissionFamilyName || ''
  },
  getFamilyPackName() {
    const familyName = this.getFamilyName()
    if (this.submissionBinaries.length > 1) {
      return familyName.length > 0 ? `${this.getFamilyName()} Complete Family` : familyName
    } else {
      return familyName.length > 0 ? `${this.getFamilyName()}` : familyName
    }
  },
  getStyleName(name, binary) {
    return name.length === 0 ? binary.fontInfo?.styleName || '' : name
  },
  getViewPrices(values) {
    /**
     * Add parameters to prices for view functionality.
     */
    const prices = []
    /**
     * Set prices for each currency.
     */
    this.currencies.options.forEach((c, idx) => {
      /**
       * Find currency price.
       */
      const value = values.filter(item => item.currency === c.acronym)[0] || {}
      /**
       * Add to prices.
       */
      prices.push({
        currency: c.acronym,
        price: this.getDigitsPrice(value.price),
        sync: value.sync === true
      })
    })
    /**
     * Return {Array}
     */
    return prices
  },
  getApiPrices(values) {
    /**
     * Set prices objects for submission that can be passed to API.
     */
    const prices = []
    for (const value of values) {
      const price = this.getApiPrice(value.price)
      if (price !== null) {
        prices.push({
          currency: value.currency,
          price
        })
      }
    }
    /**
     * Return {Array}
     */
    return prices
  },
  getDigitsPrice(value) {
    /**
     * Returns price with digits.
     * For amount of 0, NaN or invalid currency it returns empty string.
     */
    const price = parseFloat(value)
    const nan = isNaN(value * 1) || isNaN(price)
    /**
     * Return {String}
     */
    return !nan ? price.toFixed(2) : ''
  },
  getApiPrice(value) {
    /**
     * Can transform string values to number.
     *
     * Parse.
     */
    const price = parseFloat(value)
    const nan = isNaN(value * 1) || isNaN(price)
    /**
     * In case value is NaN.
     * Return {null}
     */
    if (nan) {
      return null
    }
    /**
     * If valid number.
     * Return {Number}
     */
    return price
  },
  getConvertedAmount(currency, amount) {
    /**
     * Returns given amount in given currency.
     */
    let c = ''
    const conv = this.$store.getters['packages/conversions'].filter(c => c.currency === currency)[0]
    /**
     * Only convert valid amounts.
     */
    if (amount?.toString()?.match(/^[0-9.]+$/) !== null && typeof conv === 'object') {
      c = this.getDigitsPrice(this.myFontsRound99(amount * conv.rate))
    }
    /**
     * Return {String}
     */
    return c
  },
  validateFamilySinglePack(currency, currAmount, family$Price) {
    if (currAmount.length === 0) {
      return this.getConvertedAmount(currency, family$Price)
    }
    return currAmount
  },

  getSingleStylePriceList(packs, currency) {
    const packages = [...packs.singleStyles, ...packs.customPackages]
    const currencySinglePackage = packages
      .map(val => {
        if (!isEmpty(currency)) {
          return val?.prices?.filter?.(item => item.currency === currency)[0]?.price
        }
      })
      .filter(value => value === '')
    if (currencySinglePackage.length > 0) {
      return packages.map(val => this.getConvertedAmount(currency, val.prices[0]?.price)).filter(v => v.length > 0)
    }
    return []
  },
  editPrices(pack) {
    pack.editing = true
  },
  /**
   * Implement MyFonts rounding algorithm for prices
   *
   * in MySQL it is:
   *
   --------------------
   DELIMITER $$
   DROP FUNCTION IF EXISTS `fn_Round99`$$

   CREATE FUNCTION `fn_Round99`(price decimal(9,2))
     RETURNS decimal(9,2)
   BEGIN
     if round(price,2) = 0 then SET price = 0.0;
     elseif price - floor(price) >= 0.8 then SET price = floor(price) +0.99;
     else
     SET price = floor(price)-1.0 +0.99;
     end if;
     RETURN round(price,2);
   END$$
   DELIMITER ;
   --------------------
   *
   * e.g.: SELECT fn_Round99(20.45);
   *
   */
  myFontsRound99(val) {
    const roundToTwo = +(Math.round(Number(val) * 100) / 100)
    let newVal = 0
    if (roundToTwo !== 0) {
      if (roundToTwo - Math.floor(val) >= 0.8) {
        newVal = Math.floor(val) + 0.99
      } else {
        newVal = Math.floor(val) - 1.0 + 0.99
        /**
         * add (edge case) exception rule
         * to prevent converted zero prices for base prices less than 0.8
         */
        newVal = newVal <= 0 && val > 0 ? 0.99 : newVal
      }
    }
    return newVal // .toFixed(digits)
  },
  editSingleStyle(key, idx) {
    this.packages.singleStyles[key].editing = true
    this.packages.singleStyles[key].prices[idx].sync = true
  },
  editDone() {
    this.packages.familyPackage.editing = false
    this.packages.singleStyles.forEach(pack => {
      pack.editing = false
    })
    this.packages.customPackages.forEach(pack => {
      pack.editing = false
    })
  },
  removePricesErrorFromSubmission() {
    this.packages.singleStyles.forEach((style, index) => {
      submissionErrors.value = submissionErrors.value.filter(
        err => !Object.keys(err)[0].includes(`currency style ${index}`)
      )
    })
    this.packages.familyPackage.prices?.forEach(() => {
      submissionErrors.value = submissionErrors.value.filter(err => !Object.keys(err)[0].includes('family_currency 0'))
      submissionErrors.value = submissionErrors.value.filter(err => !Object.keys(err)[0].includes('currency custom'))
    })
  },
  savePrices(key, idx, customId) {
    this.removePricesErrorFromSubmission()
    this.editDone()
    this.commitChange()
    if (typeof key === 'number' && typeof idx === 'number') {
      this.resetSyncTimer.clear()
      this.resetSyncTimer.set(() => {
        this.resetSyncState(key, idx)
      }, 150)
    }
  },
  validatePrices(e, currency, familyPackagePrice, familyPackagePrice0) {
    // Fetch label name from e.target (Vanilla way: use e.target.labels[0].innerText if present)
    let fieldName = '';
    if (e?.target?.labels?.length > 0) {
      fieldName = e.target.labels[0].innerText;
    } else if (e?.target?.getAttribute?.('label')) {
      fieldName = e.target.getAttribute('label');
    } else {
      fieldName = 'This field';
    }

    let value = e?.target?.value ?? '';
    let familyPackprice = this.validateFamilySinglePack(currency, familyPackagePrice, familyPackagePrice0);
    let fieldKey = e.target.name;
    if (parseFloat(value) > parseFloat(familyPackprice)) {
      this.pushErrorToSubmissionErrors(fieldKey, `The ${fieldName} is greater than the complete family pack price.`);
    } else if (parseFloat(value) > 999999) {
      this.pushErrorToSubmissionErrors(fieldKey, `The ${fieldName} must be 999999 or less.`);
    } else {
      const errorExists = Object.hasOwn(this.errors, fieldKey);
      // Clear error if validation passes
      if (errorExists) {
        delete this.errors[fieldKey]
        this.setErrors(this.errors)
      }
    }
    return true
  },
  validateAndSavePrices(e, key, id, currency, familyPackagePrice, familyPackagePrice0, onlyValidate = false) {
    const isvalid = this.validatePrices(e, currency, familyPackagePrice, familyPackagePrice0)
    if (isvalid && !onlyValidate) {
      this.savePrices(key, id)
    }
  },
  pushErrorToSubmissionErrors(key, errorMessage) {
      this.errors[key] = errorMessage
      this.setErrors(this.errors)
  },
  resetSyncState(key, idx) {
    if (this.packages.singleStyles[key]) {
      this.packages.singleStyles[key].prices[idx].sync = false
    }
  },
  getSyncPriceState(key, idx) {
    /**
     * Detects wether a new value is different from the previous value.
     * Returns true if different and no field errors.
     */
    const item = this.packages.singleStyles[key].prices[idx]
    const errors = Object.hasOwn(this.errors, `currency styles.currency style ${key} ${idx}`)
    const empty = isNaN(parseFloat(item.price))
    return item.sync && !errors && !empty
  },
  syncPrice(key, idx) {
    /**
     * Apply value of specific field to entire column.
     */
    const price = this.packages.singleStyles?.[key]?.prices?.[idx]?.price;
    if (typeof price === 'string') {
      for (const style of this.packages.singleStyles) {
        style.prices[idx].price = price
      }
      if (idx > 0) {
        this.$nextTick(() => {
          for (let i = 0; i < this.packages.singleStyles.length; i++) {
            const id = this.$vId(this, `currency style ${i} ${idx}`);
            const el = document.getElementById(id)
            if (el) {
              const value = el.value
              if (value !== "" && value !== null && value !== undefined) {
                  this.validateAndSavePrices({target: el}, i, idx, this.currencies.options[idx].acronym, this.packages.familyPackage.prices[idx].price, this.packages.familyPackage.prices[0].price, true)
              }
            }
          }
        })
      }
      /**
       * Save
       */
      this.savePrices(key, idx)
    }
  },
  commitChange() {
    this.handleAllSubmissionErrors()
    this.updateSubmission('', 'subpackage-name', this.bannedWordForSubpackage)
  },
  handleAllSubmissionErrors() {
    this.packages.customPackages.forEach((item, index) => {
      const errorsAfterValidation = submissionErrors.value.filter(
        err => !err.hasOwnProperty(`currency custom${index}.custom package name${index} prices`)
      )
      submissionErrors.value = [...errorsAfterValidation, ...submissionErrors.value]
    })
  },
  buildSubmission() {
    return new Promise(resolve => {
      /**
       * Set packages in $store.
       */
      const packages = []
      const binaries = this.$store.getters['binaries/list']
      /**
       * Sync Packages
       */
      this.setPackages()
      /**
       * (1) familyPackage
       */
      const family = this.getApiFamilyPackage()
      if (family) {
        if (binaries.length > 0) {
          packages.push(family)
        }
      }
      /**
       * (2) singlePackages
       */
      for (const single of this.getApiSinglePackages()) {
        const pack = this.getBinaryProofApiPackage(single)
        if (binaries.length > 1 && pack.styles.length > 0) {
          packages.push(pack)
        }
      }
      /**
       * (3) customPackages
       */
      for (const custom of this.getApiCustomPackages()) {
        const pack = this.getBinaryProofApiPackage(custom)
        if (binaries.length > 2) {
          packages.push(pack)
        }
      }
      /**
       * Add new packages.
       */
      this.submissionPackages = packages
      /**
       * Done.
       */
      resolve()
    })
  },
  getPackageNames() {
    /**
     * Generate a list of existing package names for custom (Vee-) validation
     */
    return this.submissionPackages.map(p => p.name)
  },
  getMergedErrors(scope) {
    let all = Object.keys(this.errors)
      .filter(error => error.includes(scope))
      .map(error => this.errors[error])
    const required = all.filter(e => e.indexOf('is required.') !== -1)
    if (required.length > 1) {
      const combine = []
      required.map(item => (item.match('The (.*) field is required') || [])[1]?.trim())
        .forEach(field => {
          if (field) {
            combine.push(field);
          }
        });
      all = all.filter(e => e.indexOf('is required.') === -1)
      all = all.concat([
        `${this.$t('pages.submissions.components.prices.errors.required_errors')}: ${combine.join(', ')}`
      ])
    }
    return all
  },
  checkDoubles() {
    const binaries = []
    /**
     * Create sorted array with binary ids
     */
    this.packages.customPackages.forEach(pack => {
      if (pack.select.values) {
        binaries.push(pack.select.values.map(s => s.value).sort())
      }
    })
    /**
     * Compare packs to detect doubles
     */
    const error = binaries.some((b, key) => {
      return binaries.some((item, index) => {
        if (key !== index) {
          if (isEqual(b, item)) {
            return true
          }
        }
      })
    })
    if (error) {
      this.errors.subpackage_doubles = this.$t('pages.submissions.components.prices.custom.unique_content_error')
    } else {
      this.errors.subpackage_doubles = null
    }
    this.setErrors(this.errors)
  },
  setSubscriptions() {
    /**
     * Listen to $store mutations.
     */
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'binaries/getAllComplete') {
        this.setPackages()
      }
    })
  },
  isWarning(data, warn = '') {
    if (warn === 'multiple') {
      const prices = data.map(item => item.prices[0].price)
      return prices.some(val => Number(val) < 9 && val !== '')
    }
    return Number(data) < 9 && data !== ''
  }
}
